import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, Button, Input, Textarea } from '@/components/ui';
import { ArrowLeft, Save, FileText, Loader2, Globe } from 'lucide-react';
import { clsx } from 'clsx';

interface Page {
  id: string;
  slug: string;
  title: string;
  titleTr?: string;
  content?: string;
  contentTr?: string;
  metaDescription?: string;
  metaDescriptionTr?: string;
  isPublished: boolean;
}

export function PageEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [lang, setLang] = useState<'en' | 'tr'>('en');
  const [formData, setFormData] = useState<Partial<Page>>({
    slug: '',
    title: '',
    titleTr: '',
    content: '',
    contentTr: '',
    metaDescription: '',
    metaDescriptionTr: '',
    isPublished: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: page, isLoading } = useQuery<Page>({
    queryKey: ['page', id],
    queryFn: async () => {
      const response = await api.get(`/admin/pages/${id}`);
      return response.data?.data;
    },
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (page) {
      setFormData(page);
    }
  }, [page]);

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Page>) => {
      if (isNew) {
        return api.post('/admin/pages', data);
      }
      return api.put(`/admin/pages/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      navigate('/pages');
    },
    onError: (error: Error & { response?: { data?: { errors?: Record<string, string> } } }) => {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    },
  });

  function handleChange(field: keyof Page, value: string | boolean) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function generateSlug(title: string) {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate(formData);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/pages"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isNew ? 'Add Page' : `Edit: ${page?.title}`}
            </h1>
            <p className="text-gray-400 mt-1">
              {isNew ? 'Create a new content page' : 'Update page content'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/pages')}>
            Cancel
          </Button>
          <Button type="submit" loading={saveMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {isNew ? 'Create Page' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Language Toggle */}
          <Card padding="md">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-400 mr-4">Language:</span>
              <button
                type="button"
                onClick={() => setLang('en')}
                className={clsx(
                  'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                  lang === 'en'
                    ? 'bg-gold text-midnight'
                    : 'bg-midnight-100 text-gray-400 hover:text-white'
                )}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLang('tr')}
                className={clsx(
                  'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                  lang === 'tr'
                    ? 'bg-gold text-midnight'
                    : 'bg-midnight-100 text-gray-400 hover:text-white'
                )}
              >
                Turkish
              </button>
            </div>
          </Card>

          {/* Page Info */}
          <Card padding="lg">
            <CardHeader title="Page Information" />
            <div className="space-y-4">
              <Input
                label="URL Slug"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                error={errors.slug}
                helper="Used in the page URL (e.g., /about)"
                required
              />

              {lang === 'en' ? (
                <>
                  <Input
                    label="Title"
                    value={formData.title}
                    onChange={(e) => {
                      handleChange('title', e.target.value);
                      if (isNew && !formData.slug) {
                        handleChange('slug', generateSlug(e.target.value));
                      }
                    }}
                    error={errors.title}
                    required
                  />
                  <Textarea
                    label="Meta Description"
                    value={formData.metaDescription}
                    onChange={(e) => handleChange('metaDescription', e.target.value)}
                    rows={2}
                    helper="SEO description (160 chars max)"
                  />
                </>
              ) : (
                <>
                  <Input
                    label="Title (Turkish)"
                    value={formData.titleTr}
                    onChange={(e) => handleChange('titleTr', e.target.value)}
                  />
                  <Textarea
                    label="Meta Description (Turkish)"
                    value={formData.metaDescriptionTr}
                    onChange={(e) => handleChange('metaDescriptionTr', e.target.value)}
                    rows={2}
                  />
                </>
              )}
            </div>
          </Card>

          {/* Content Editor */}
          <Card padding="lg">
            <CardHeader
              title={lang === 'en' ? 'Content' : 'Content (Turkish)'}
              description="Page content in markdown format"
            />
            <Textarea
              value={lang === 'en' ? formData.content : formData.contentTr}
              onChange={(e) =>
                handleChange(lang === 'en' ? 'content' : 'contentTr', e.target.value)
              }
              rows={20}
              className="font-mono text-sm"
              placeholder="# Heading&#10;&#10;Your content here..."
            />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card padding="lg">
            <CardHeader title="Publishing" />
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isPublished}
                  onChange={(e) => handleChange('isPublished', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-midnight-100 text-gold focus:ring-gold"
                />
                <div>
                  <span className="text-white font-medium">Published</span>
                  <p className="text-sm text-gray-400">Make this page visible</p>
                </div>
              </label>
            </div>
          </Card>

          <Card padding="lg">
            <CardHeader title="Preview" />
            <div className="rounded-lg bg-midnight-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-400">/{formData.slug || 'page-url'}</span>
              </div>
              <h3 className="text-white font-semibold">
                {lang === 'en' ? formData.title : formData.titleTr || formData.title || 'Page Title'}
              </h3>
              <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                {lang === 'en'
                  ? formData.metaDescription
                  : formData.metaDescriptionTr || formData.metaDescription || 'No description'}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </form>
  );
}
